/* jshint esversion: 8 */
/* jshint node: true */

import React, { useEffect, useState } from "react";
import SecondaryPageContainer from "components/SecondaryPageContainer/SecondaryPageContainer";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import * as common from "common";
import ReactGA from "react-ga4";
import PropTypes from "prop-types";
import { createSidebarLink } from "services/SpotlightTheme";
import { useSelector } from "react-redux";
import { selectTheme } from "stateMgmt/appSlice";
import "./datasets.scss";
import logger from "services/LoggingService";

const s3path = common.getS3Url();

const DatasetsPage = (props) => {
  const [tocData, setTocData] = useState([]);
  const theme = useSelector(selectTheme);

  ReactGA.send({
    hitType: "pageview",
    page: "/components/MainContentArea/DatasetsPage/DatasetsPage.js",
    title: "Datasets",
  });

  const pageContents = (
    <div
      className="row DATASETS"
      id="Datasets_page-top"
      data-testid="Datasets_Page"
    >
      <div className="text-left">
        <Typography variant="h1" id="page-heading" role="heading" tabIndex={0}>
          President&apos;s Emergency Plan for AIDS Relief Reporting (PEPFAR)
          Datasets
        </Typography>

        <Typography
         variant="body1"
        >
          A subset of historic Spotlight datasets through Fiscal Year (FY) 2025, 
          compliant with Executive Orders, are available below. These datasets include PEPFAR Financial 
          Management data, PEPFAR Monitoring, Evaluation, and Reporting (MER) indicator data, 
          and PEPFAR Human Resources for Health (HRH) Inventory data.          
         
        </Typography>
        <Typography variant="body1">
          For FY2025 MER results, only Q4 data are included in these datasets. FY2025 targets and budgets as approved through congressional notification procedures are included in these datasets; however, program interruptions, reporting challenges, and programmatic shifts have occurred since January 2025. FY2025 target, budget, results, and expenditure data should be analyzed and interpreted accordingly.
        </Typography> 

        <Typography variant="body1">
          These datasets are downloaded as compressed (zip) files, which contain text files (txt, tab delimited) that can be imported into a data analysis tool for analysis and review. Reference  documents can be found within each programmatic section.
        </Typography>
        <Typography variant="body1">
          For more information on PEPFAR Spotlight datasets, including abbreviated data dictionaries for each PEPFAR data stream, please review the

                <Link
                    component={require('react-router-dom').Link}
                    to="/user_guide"
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                     target="_blank"
                  >Spotlight User Guide
                  </Link>.
        </Typography>

         <section className="section" id="FMD">

          <Typography variant="h2" role="heading" tabIndex={0} style={{ color: theme.section }}>
            Financial Management Datasets
          </Typography>
          <Typography >
            PEPFAR Financial Management Datasets include data on budgets and expenditures. Budgets are set by country teams in their Country and Regional Operational Plans (COP/ROP) and represent the total new and applied pipeline financial resources that an implementing mechanism plans to spend to achieve approved targets and other COP/ROP activities during the 12-month fiscal year (October 1 – September 30).
          </Typography>
          <Typography >
            For more information on PEPFAR Financial Data, please review the 
            <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href="https://help.datim.org/hc/en-us/articles/36621447090964-PEPFAR-Financial-Classification-Reference-Guide"
                  target="_blank"
                >
                PEPFAR Financial Classifications Reference Guide</Link>
            . The Spotlight User Guide defines common variables used in the dataset.  
          </Typography>
            

          <br />
          {/* PEPFAR OU Commodities Procurement */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography variant="h3" role="heading" >
                PEPFAR OU Commodities Procurement
              </Typography>
              <Typography>
                The Spotlight public commodities dataset provides, at an OU level, what was budgeted for commodities procurement from FY2019 – 2025. Procurements are classified using the PEPFAR Major and Minor Category classification and a standardized item name. In addition to the planned commodity budget, the dataset includes planned procurement quantities.
              </Typography>
            </div>
            
            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}fin_mngt/PEPFAR_OU_Commodities_Procurements.zip`}                 
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PEPFAR OU Commodities Procurements.zip
                </Link>
              </Typography>
            </div>
          </div>
          <br />
          {/* PEPFAR OU Budgets and Expenditures */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography variant="h3" role="heading" >
                PEPFAR Operating Unit (OU) Budgets and Expenditures
              </Typography>
              <Typography variant="body1">
                The PEPFAR Operating Unit (OU) Budgets and Expenditures dataset provides, at an OU level, budgets (FY2004-2025) and expenditures (FY2018-2025) by Program Area, Sub-Program Area, and Interaction Type. Financial data is further disaggregated pre-FY2024 and earlier by Beneficiary and Sub Beneficiary and from FY2024 onwards by Targeted Beneficiary.
                Budget data are further disaggregated by Record Type (Implementing Mechanism vs. Management & Operations), and an indication of whether the funding is for Economic Support Fund (American Rescue Plan Act) Funding (Yes/No).
                Expenditures are further disaggregated by Cost Category and Sub-Cost Category.
              </Typography>
              <Typography variant="body1"> 
               A separate CSV dataset and accompanying codebook are included for PEPFAR Expenditures FY2015 – 2017, which were reported through expenditure analysis (EA) classifications and mapped to the new financial classifications.
              </Typography>
            </div>
            
            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}fin_mngt/PEPFAR_OU_Budgets_and_Expenditures.zip`}                   
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PEPFAR Operating Unit (OU) Budgets and Expenditures.zip
                </Link>
              </Typography>
            </div>
          </div>
          <br />
         
          {/* Public Partner Budgets and Expenditures */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Public Partner Budgets and Expenditures
              </Typography>
              <Typography variant="body1">
                The Public Partner Budget and Expenditures dataset provides mechanism-level budget and expenditure data for FY2021 – 2025 by the PEPFAR Financial Classifications. Data are further disaggregated by Program, Sub-Program, Interaction Type, pre-FY2024 by Beneficiary and Sub-Beneficiary, and FY2024 and onwards by Targeted Beneficiary. 
              </Typography>
              <Typography variant="body1">                
                Please note that for any TBD partners, some mechanism details are redacted, and budgets are aggregated to the OU by agency level such that the amount for each TBD mechanism is not known.              
                Steps have been taken to mask Program Management (PM) data, which may result in minor rounding errors of $1-$2 USD. Please refer to the Spotlight User Guide for additional information about masking of PM data.
              </Typography>
              
            </div>
            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}fin_mngt/Public_Partner_Budgets_and_Expenditures.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public Partner Budgets and Expenditures.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />

          {/* ------------------------------------- */}
          {/* -- END OF FIN                         */}
          {/* ------------------------------------- */}
        </section>

        <section className="section" id="MDD">
          <div role="heading" tabIndex={0}>
            <Typography variant="h2" style={{ color: theme.section }}>
              Monitoring, Evaluation, and Reporting (MER) Downloadable Datasets
            </Typography>

            <Typography variant="body1">
              The Monitoring, Evaluation, and Reporting (MER) Datasets provide a view of an Operating Unit&apos;s (OU) targets and results against a selection of indicators that demonstrate PEPFAR&apos;s progress and contributions to combat global HIV/AIDS. The targets and results presented in these datasets represent the collective targets and achievements for all U.S. Government PEPFAR implementing agencies. 
            </Typography>
            <Typography variant="body1">
              For more information on the indicators by which targets and results are reported, please review the 
              <Link
                variant="body2"
                underline="hover"
                style={{ color: theme.linkColor, fontWeight: 600 }}
                href="https://help.datim.org/hc/en-us/articles/360000084446-MER-Indicator-Reference-Guides"
                target="_blank"
                rel="noopener noreferrer"
              >
                MER Indicator Reference Guide
              </Link>. The 
               <Link
                    component={require('react-router-dom').Link}
                    to="/user_guide"
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 600 }}
                     target="_blank"
                  >Spotlight User Guide&nbsp; 
                  </Link>  defines common variables used in the dataset.  
            </Typography>
            <Typography variant="body1">
              <strong>Please note for MER datasets:</strong> Due to data quality improvement efforts, Nigeria FY2023 Quarter 4 (Q4) MER data are excluded from all datasets. For FY2025, only Q4 results are included in these datasets. Data should be analyzed and interpreted accordingly.
            </Typography>
          </div>
          <br />

          {/* Enhanced_Geographical_Analysis */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Enhanced Geographical Analysis &ndash; Sub-National Unit
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 – 2024 and FY2025 Q4) and annual targets (FY2016 – 2025) by Operating Unit and Sub-National Units for a comprehensive array of indicators across PEPFAR&apos;s program areas.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Enhanced_Geographical_Analysis.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enhanced Geographical Analysis.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Operating_Unit_by_Fine_Age_and_Sex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Operating Unit (OU) by Fine Age & Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2019 – 2024 and FY2025 Q4) and annual targets (FY2019 – 2025) by Operating Unit, Fine Age and Sex. Note: Reporting for age bands above 50 is optional and not available for all OUs; however, the aggregate 50+ age band is available for all OUs.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Operating_Unit_by_Fine_Age_and_Sex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Operating Unit (OU) by Fine Age & Sex.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Country_Level_Results_by_Modality_Coarse_Age_and_Sex */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography
                variant="h3"
                role="heading"
                
              >
                Country Level Results by Modality, Coarse Age and Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2018 – 2024 and FY2025 Q4) and annual targets (FY2018 – 2025) by Operating Unit, Country, Testing Modality, Coarse Age, and Sex. Testing modality reporting was optional in FY2025.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="h3">
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}mer_downloadables/Country_Level_Results_by_Modality_Coarse_Age_and_Sex.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Country Level Results by Modality, Coarse Age and Sex.zip
                  </Link>
                </Typography>
              </Typography>
            </div>
          </div>

          <br />
          {/* Clinical_Cascade_Results_by_Coarse_Age_and_Sex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Clinical Cascade – Results by Coarse Age and Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 – 2024 and FY2025 Q4) and annual targets (FY2016 – 2025) by Operating Unit, Sub-National Unit, Coarse Age and Sex for a subset of Testing and Treatment indicators representing the Clinical Cascade.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 600 }}
                  href={`${s3path}mer_downloadables/Clinical_Cascade_Results_by_Coarse_Age_and_Sex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Clinical Cascade Results by Coarse Age and Sex.zip
                </Link>
              </Typography>
            </div>
          </div>

          <br />
          {/* Clinical_Cascade_Results_by_Fine_Age_and_Sex */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography
                variant="h3"
                role="heading"
                
              >
                Clinical Cascade – Results by Fine Age and Sex
              </Typography>
              <Typography>
                Quarterly MER results (FY2019 – 2024 and FY2025 Q4) and annual targets (FY2019 – 2025) by Operating Unit, Sub-National Unit 1, Fine Age, Coarse Age, and Sex for a subset of Testing and Treatment indicators representing the Clinical Cascade.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="h3">
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}mer_downloadables/Clinical_Cascade_Results_by_Fine_Age_and_Sex.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Clinical Cascade Results by Fine Age and Sex.zip
                  </Link>
                </Typography>
              </Typography>
            </div>
          </div>

          <br />
          {/*  Site_Performance */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography
                variant="h3"
                role="heading"
                
              >
                Site Performance
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 – 2024 and FY2025 Q4) and annual targets (FY2016 – 2025) by Operating Unit, Priority Sub-National Unit (PSNU), and masked Facility/Site for commonly used indicators across PEPFAR’s Program Areas.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="h3">
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}mer_downloadables/Site_Performance_2021_plus.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Site Performance 2021 plus.zip
                  </Link>
                </Typography>
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}mer_downloadables/Site_Performance_2016-2020.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Site Performance 2016-2020.zip
                  </Link>
                </Typography>
              </Typography>
            </div>
          </div>

          <br />
          {/* Partner Performance */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography
                variant="h3"
                role="heading"
                
              >
                Partner Performance
              </Typography>
              <Typography>
                Quarterly MER results (FY2016 – 2024 and FY2025 Q4) and annual targets (FY2016 – 2025) by Operating Unit, PSNU, masked Implementing Partner and Funding Agency for commonly used indicators across PEPFAR’s Program Areas.
              </Typography>
            </div>

            <div role="navigation">
              <Typography variant="h3">
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}mer_downloadables/Partner_Performance.zip `}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Partner Performance.zip
                  </Link>
                </Typography>
              </Typography>
            </div>
          </div>

          <br />
          {/* Public_Partner_MER_AgeSex */}
          <div>
            <div role="heading" tabIndex={0}>
              <Typography variant="h3" role="heading">
                Public Partner MER Dataset
              </Typography>
              <Typography variant="body1"> 
                Quarterly Implementing Partner (IP) MER results (FY2018 – 2024 and FY2025 Q4) and annual targets (FY2018 – 2025) are presented in two datasets: 1) Data aggregated at the total numerator and total denominator levels (Public Partner TopLine dataset), and 2) data disaggregated by coarse Age and Sex (Public Partner Age Sex dataset).
              </Typography>            
              <Typography variant="body1">
                MER data are not included for any partners which are TBD or not active. When two IPs operate at the same facility, any duplicate results reported at that facility are manually &apos;deduplicated&apos; by PEPFAR staff to generate an aggregate total of individuals reached at the facility. This aggregate total is used to produce country-level results. Due to the nature of this data set at the partner level, these manual deduplication adjustments have been removed from this dataset so IP data can be reviewed in full.&nbsp;
                <strong>Do not aggregate partner data to generate total country or SNU results.</strong>{" "}
              </Typography>
            </div>
            <div role="heading">
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}additionalData/partner-mer-data/Public_Partner_MER_TopLine.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public Partner MER TopLine.zip
                </Link>
              </Typography>
              <Typography variant="body1">
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href={`${s3path}additionalData/partner-mer-data/Public_Partner_MER_AgeSex.zip`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Public Partner MER AgeSex.zip
                </Link>
              </Typography>
            </div>
            </div>
          <br />
             {/* Voluntary Male Medical Circumcisions */}
          <div>
            <div role="heading" tabIndex={0}>
          <Typography variant="h3" role="heading">
            Voluntary Male Medical Circumcisions
          </Typography>
          <Typography>
            Annual results by operating unit for FY2007 – 2024.
          </Typography>
          <div role="navigation">
            <Typography variant="body1">
              <Link
                variant="body2"
                underline="hover"
                style={{ color: theme.linkColor, fontWeight: 500 }}
                href={`${s3path}mer_downloadables/Voluntary_Male_Medical_Circumcisions_Results_by_Operating_Unit.zip`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Voluntary Male Medical Circumcisions Results by Operating Unit.zip
              </Link>
            </Typography>
          </div>
        </div>
          </div>
        </section>

        
        <br />

        {/* ------------------------------------- */}
        {/* -- END OF MER                         */}
        {/* ------------------------------------- */}

        <section className="section" id="HRH">
          <Typography variant="h2" role="heading" tabIndex={0} style={{ color: theme.section }}>
            Human Resources for Health (HRH) Inventory
          </Typography>
          {/*  Human Resources for Health (HRH) Inventory */}
          <div>
            <div role="note" tabIndex={0}>
              <Typography variant="body1">
                The PEPFAR Human Resources for Health (HRH) Inventory dataset provides information on PEPFAR investments in HRH across all Operating Units (i.e., PEPFAR-supported countries and regional programs) during Fiscal Year 2025. These data can be used to aid strategic planning and decision-making related to PEPFAR investments in HRH. 
              </Typography>
              <Typography variant="body1">
                For more information about the HRH Inventory, please refer to the 
                <Link
                  variant="body2"
                  underline="hover"
                  style={{ color: theme.linkColor, fontWeight: 500 }}
                  href="https://help.datim.org/hc/en-us/articles/41709406760980-FY25-PEPFAR-Human-Resources-for-Health-HRH-Inventory-Handbook"
                  target="_blank"
                >
                HRH Inventory Handbook.</Link> The
                <Link
                    component={require('react-router-dom').Link}
                    to="/user_guide"
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                     target="_blank"
                  >Spotlight User Guide
                  </Link> defines the variables used in the dataset, including notes on intended use, where applicable.
              </Typography>
            
               <Typography variant="body1">
                  <strong>HRH Inventory Public Dataset:</strong>
                  &nbsp; This dataset describes healthcare workers that PEPFAR funding supports in every PEPFAR-supported Operating Unit (OU) by six key variables: category, cadre, program, sub-program, interaction type, actual annual spend.
                </Typography>
                
             
            </div>

            <div role="navigation">
              <Typography variant="h3">
                
                <Typography variant="body1">
                  <Link
                    variant="body2"
                    underline="hover"
                    style={{ color: theme.linkColor, fontWeight: 500 }}
                    href={`${s3path}hrh_downloadables/HRH_Inventory_Public_Dataset.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HRH Inventory Public Dataset.zip
                  </Link>
                </Typography>
               
              </Typography>
            </div>
          </div>
        </section>

       
      </div>
    </div>
  );

  useEffect(() => {
    renderTOC();
  }, []);

  function renderTOC() {
    let sectionElement, headerElement, title, link;
    const sectionHeaders = document.getElementsByClassName("section");
    const links = [];
    for (let i = 0; i < sectionHeaders.length; i++) {
      logger.debug(`create link ${i}`);
      sectionElement = sectionHeaders[i];
      headerElement = sectionElement.getElementsByTagName("h2")[0];
      if (headerElement) {
        title = headerElement.innerText;
        link = "#" + sectionElement.id;
        links.push(createSidebarLink(link, title, theme.linkColor));
      }
    }
    setTocData(links);
  }

  return (
    <SecondaryPageContainer
      contents={pageContents}
      showToc={true}
      pageHeaderId="page-heading"
      isSidebarCollapsed={props.isSidebarCollapsed}
      data={tocData}
    />
  );
};

DatasetsPage.propTypes = {
  isSidebarCollapsed: PropTypes.bool.isRequired,
};

export default DatasetsPage;
